"use client";

import { CLM } from "@/common/schema/claims";
import { dateTime, DateTime } from "@/fx/data-helpers";
import assert from "assert";
import { useSession } from "next-auth/react";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

export namespace CTX {
  export type LoggedInContext = {
    currentUser:      CLM.User
    superUser:        CLM.User
    userTestingTags?: string[]
  }
  export type AppContext = {
    currentApp:        CLM.App | undefined
    isAppNoAccessPage: boolean
  }
  export type Service = {
    useCurrentUser:     () => CLM.User | undefined,
    useIsLoggedIn:      () => boolean,
    useLoggedInContext: () => LoggedInContext,
    useCurrentApp:      () => AppContext,
    now:                () => DateTime,
  }
}
export function context(): CTX.Service {
  function useClientSession() {
    const session = useSession();
    assert(session, "Session is not defined");
    assert(session.data, "Session data is not defined");
    return session.data;
  }
  function useCurrentUser(): CLM.User | undefined {
    const session_ = useSession();
    if(!session_.data) return undefined;
    const session = session_.data;
    assert(session.user,             "User is not defined");
    if (session.user.currentUser === undefined) return undefined;
    const rawUser = session.user.currentUser;
    return CLM.fromRawUser()(rawUser);
  }
  function useIsLoggedIn() {
    const session = useClientSession();
    assert(session, "Session is not defined");
    return session.user.currentUser != undefined && session.user.currentUser.roleType !== undefined;
  }
  function useLoggedInContext(): CTX.LoggedInContext {
    const session = useClientSession();
    assert(session.user,             "User is not defined");
    assert(session.user.currentUser, "Current user is not defined");
    const rawUser = session.user.currentUser;
    const rawSuperUser = session.user.superUser ?? session.user.currentUser;
    const currentUser = CLM.fromRawUser()(rawUser);
    const superUser   = CLM.fromRawUser()(rawSuperUser);
    return {
      currentUser,
      superUser,
      userTestingTags: Array.from(new Set([
        ...(currentUser.testingTags?.map(_ => _.identifier) ?? []),
        ...(superUser.testingTags?.map(_ => _.identifier) ?? []),
      ])),
    };
  }
  function useCurrentApp(): CTX.AppContext {
    const [hostname, setHostname] = useState('');

    useEffect(() => {
      setHostname(window.location.hostname);
    }, []);
    const pathname          = usePathname();
    const currentAppInfo = CLM.currentAppInfo({ hostname, pathname });

    return currentAppInfo;
  }
  function now(): DateTime {
    return dateTime(new Date());
  }

  return { useIsLoggedIn, useCurrentUser, useLoggedInContext, useCurrentApp, now };
}
